// src/pages/Organizations/Marketing/components/ContentGenerator.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
  IconButton,
  Tabs,
  Tab,
  Divider,
  Chip,
  FormControlLabel,
  Checkbox,
  ListItem,
  ListItemText,
  ListItemIcon,
  OutlinedInput,
  Snackbar,
  Alert,
  useTheme,
  Card,
  CardContent,
  CardActions,
  List,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EventIcon from '@mui/icons-material/Event';
import CardTravelIcon from '@mui/icons-material/CardTravel';
import PeopleIcon from '@mui/icons-material/People';
import PublicIcon from '@mui/icons-material/Public';
import { v4 as uuidv4 } from 'uuid';
import { generateMarketingContent, LeonardoContentGenerationOptions } from '../../../../services/leonardoAIService';
import { generateTextContent, StabilityTextGenerationOptions } from '../../../../services/stabilityTextService';
import { useMarketingStudio, MarketingAsset } from '../../../../contexts/MarketingStudioContext';
import { Event } from '../../../../types/Event';
import { Trip } from '../../../../types/Trip';
import { getUserEvents } from '../../../../services/eventService';
import { getUserTrips } from '../../../../services/tripService';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../../firebase/config';

interface ContentGeneratorProps {
  organizationId: string;
  organizationName: string;
  branding: {
    logo?: string;
    primaryColor: string;
    secondaryColor: string;
    fontFamily: string;
    brandVoice: string;
    targetAudience: string;
    industry: string;
  };
}

type ContentType = 'social' | 'email' | 'blog' | 'ad' | 'newsletter';

interface ContentTemplate {
  name: string;
  description: string;
  contentType: ContentType;
  prompt: string;
}

// Content templates by type
const contentTemplates: Record<ContentType, ContentTemplate[]> = {
  social: [
    {
      name: 'Engagement Post',
      description: 'Create a social post that drives user engagement',
      contentType: 'social',
      prompt: 'Create an engaging social media post that asks a question to our audience',
    },
    {
      name: 'Product Launch',
      description: 'Announce a new product or service',
      contentType: 'social',
      prompt: 'Write a social media post announcing an exciting new product launch',
    },
    {
      name: 'Social Promotion',
      description: 'Promote a sale or special offer',
      contentType: 'social',
      prompt: 'Create a social media post promoting a limited-time special offer',
    },
  ],
  email: [
    {
      name: 'Welcome Email',
      description: 'Welcome new subscribers or customers',
      contentType: 'email',
      prompt: 'Write a friendly welcome email for new subscribers',
    },
    {
      name: 'Promotional Email',
      description: 'Promote products or services',
      contentType: 'email',
      prompt: 'Write a promotional email highlighting our top services and a special discount',
    },
  ],
  blog: [
    {
      name: 'How-To Guide',
      description: 'Step-by-step instructions',
      contentType: 'blog',
      prompt: 'Write a how-to guide about best practices in our industry',
    },
    {
      name: 'Industry Insights',
      description: 'Thought leadership and expert analysis',
      contentType: 'blog',
      prompt: 'Create a blog post about the latest trends and insights in our industry',
    },
    {
      name: 'Case Study',
      description: 'Success stories and examples',
      contentType: 'blog',
      prompt: 'Write a case study template highlighting a customer success story',
    },
  ],
  ad: [
    {
      name: 'Search Ad',
      description: 'Concise ads for search engines',
      contentType: 'ad',
      prompt: 'Write a compelling search ad with headline and description',
    },
    {
      name: 'Display Ad',
      description: 'Visual ads with strong CTAs',
      contentType: 'ad',
      prompt: 'Create copy for a display ad highlighting our key benefits',
    },
    {
      name: 'Video Script',
      description: 'Script for video advertisements',
      contentType: 'ad',
      prompt: 'Write a 30-second video ad script that introduces our brand',
    },
  ],
  newsletter: [
    {
      name: 'Monthly Newsletter',
      description: 'Regular updates and news',
      contentType: 'newsletter',
      prompt: 'Create a monthly newsletter template with sections for news, updates, and featured content',
    },
  ],
};

const ContentGenerator: React.FC<ContentGeneratorProps> = ({ organizationId, organizationName, branding }) => {
  const theme = useTheme();
  const { marketingAssets, addMarketingAsset, deleteMarketingAsset } = useMarketingStudio();
  
  // Content generation state
  const [contentType, setContentType] = useState<ContentType>('social');
  const [selectedTemplate, setSelectedTemplate] = useState<ContentTemplate | null>(contentTemplates.social[0]);
  const [prompt, setPrompt] = useState(contentTemplates.social[0].prompt);
  const [tone, setTone] = useState<'formal' | 'casual' | 'professional' | 'friendly' | 'persuasive'>('professional');
  const [wordCount, setWordCount] = useState<number>(150);
  const [isGenerating, setIsGenerating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<string | null>(null);
  
  // Content library state
  const [contentTabValue, setContentTabValue] = useState(0);
  const [savedContent, setSavedContent] = useState<MarketingAsset[]>([]);
  
  // Newsletter inclusion state
  const [includeEvents, setIncludeEvents] = useState(false);
  const [includeTrips, setIncludeTrips] = useState(false);
  const [includeMemberships, setIncludeMemberships] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [selectedTrips, setSelectedTrips] = useState<string[]>([]);
  const [selectedMemberships, setSelectedMemberships] = useState<string[]>([]);
  
  // Data state
  const [events, setEvents] = useState<Event[]>([]);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [memberships, setMemberships] = useState<any[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  
  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // Fetch organization data
  useEffect(() => {
    const fetchOrganizationData = async () => {
      if (!organizationId) return;
      
      setLoadingData(true);
      try {
        // Fetch events for this organization
        const eventsQuery = query(
          collection(db, 'events'),
          where('organizationId', '==', organizationId)
        );
        const eventsSnapshot = await getDocs(eventsQuery);
        const eventsData: Event[] = [];
        eventsSnapshot.forEach(doc => {
          const event = { id: doc.id, ...doc.data() } as Event;
          eventsData.push(event);
        });
        setEvents(eventsData);
        
        // Fetch trips for this organization
        const tripsQuery = query(
          collection(db, 'trips'),
          where('organizationId', '==', organizationId)
        );
        const tripsSnapshot = await getDocs(tripsQuery);
        const tripsData: Trip[] = [];
        tripsSnapshot.forEach(doc => {
          const trip = { id: doc.id, ...doc.data() } as Trip;
          tripsData.push(trip);
        });
        setTrips(tripsData);
        
        // Fetch memberships/subscriptions for this organization
        const membershipsQuery = query(
          collection(db, 'subscriptionTiers'),
          where('organizationId', '==', organizationId)
        );
        const membershipsSnapshot = await getDocs(membershipsQuery);
        const membershipsData: any[] = [];
        membershipsSnapshot.forEach(doc => {
          const membership = { id: doc.id, ...doc.data() };
          membershipsData.push(membership);
        });
        setMemberships(membershipsData);
        
      } catch (error) {
        console.error('Error fetching organization data:', error);
        setError('Failed to load organization data.');
      } finally {
        setLoadingData(false);
      }
    };
    
    fetchOrganizationData();
  }, [organizationId]);
  
  // Handle content type change
  const handleContentTypeChange = (event: SelectChangeEvent) => {
    const newType = event.target.value as ContentType;
    setContentType(newType);
    setSelectedTemplate(contentTemplates[newType][0]);
    setPrompt(contentTemplates[newType][0].prompt);
  };
  
  // Handle tone change
  const handleToneChange = (event: SelectChangeEvent) => {
    setTone(event.target.value as 'formal' | 'casual' | 'professional' | 'friendly' | 'persuasive');
  };
  
  // Handle template selection
  const handleTemplateSelect = (template: ContentTemplate) => {
    setSelectedTemplate(template);
    setPrompt(template.prompt);
  };
  
  // Handle content tab change
  const handleContentTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setContentTabValue(newValue);
  };
  
  // Generate content
  const handleGenerateContent = async () => {
    if (!prompt) {
      setError('Please enter a prompt');
      return;
    }
    
    setIsGenerating(true);
    setError(null);
    setGeneratedContent(null);
    
    try {
      // Collect data for content generation
      let contentData = '';
      
      // Add events data if selected
      if (includeEvents && selectedEvents.length > 0) {
        contentData += '\n\nUPCOMING EVENTS:';
        const selectedEventData = events.filter(event => selectedEvents.includes(event.id || ''));
        selectedEventData.forEach(event => {
          contentData += `\n- ${event.title}: ${event.description || 'No description'} on ${formatDate(event.startDate)}`;
          if (event.location) {
            contentData += ` at ${typeof event.location === 'string' ? event.location : event.location.name}`;
          }
          // Add event image URL for the newsletter generator
          if (event.coverImageUrl) {
            contentData += ` |IMAGE:${event.coverImageUrl}|`;
          }
        });
      }
      
      // Add trips data if selected
      if (includeTrips && selectedTrips.length > 0) {
        contentData += '\n\nUPCOMING TRIPS:';
        const selectedTripData = trips.filter(trip => selectedTrips.includes(trip.id || ''));
        selectedTripData.forEach(trip => {
          contentData += `\n- ${trip.title}: ${trip.description || 'No description'} from ${formatDate(trip.startDate)} to ${formatDate(trip.endDate)}`;
          if (trip.primaryDestination) {
            contentData += ` - Destination: ${trip.primaryDestination.name}`;
          }
          // Add trip image URL for the newsletter generator
          if (trip.coverImageUrl) {
            contentData += ` |IMAGE:${trip.coverImageUrl}|`;
          }
        });
      }
      
      // Add memberships data if selected
      if (includeMemberships && selectedMemberships.length > 0) {
        contentData += '\n\nMEMBERSHIP OPTIONS:';
        const selectedMembershipData = memberships.filter(membership => selectedMemberships.includes(membership.id));
        selectedMembershipData.forEach(membership => {
          contentData += `\n- ${membership.name}: ${membership.description || 'No description'} - $${membership.price} ${membership.currency}`;
          if (membership.benefits) {
            contentData += `\n  Benefits: ${membership.benefits.join(', ')}`;
          }
        });
      }
      
      // Enhance the prompt with organization data
      const enhancedPrompt = `${prompt}\n\nOrganization: ${organizationName}\nIndustry: ${branding.industry || 'Travel'}\nTarget Audience: ${branding.targetAudience || 'Travelers'}\n${contentData}`;
      
      let content = '';
      
      // For newsletter content type, use Stability AI
      if (contentType === 'newsletter') {
        // Create content generation options for Stability AI
        const options: StabilityTextGenerationOptions = {
          prompt: enhancedPrompt,
          contentType: 'newsletter',
          tone,
          wordCount: 800,  // Longer content for newsletters
          organizationName,
          industry: branding.industry,
          targetAudience: branding.targetAudience,
          maxTokens: 1500
        };
        
        // Generate newsletter content with Stability AI
        content = await generateTextContent(options);
      } else {
        // Use Leonardo AI for other content types
        const options: LeonardoContentGenerationOptions = {
          prompt: enhancedPrompt,
          contentType: contentType as 'social' | 'email' | 'blog' | 'ad',  // Type assertion to fix TypeScript error
          tone,
          wordCount,
          organizationId,
          organizationName,
          industry: branding.industry,
          targetAudience: branding.targetAudience,
        };
        
        // Generate content with Leonardo AI
        content = await generateMarketingContent(options);
      }
      
      setGeneratedContent(content);
      setSuccess(true);
    } catch (error) {
      console.error('Error generating content:', error);
      setError('Failed to generate content. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };
  
  // Helper function to format date
  const formatDate = (date: Date | string | null | any) => {
    if (!date) return 'TBD';
    
    try {
      // Handle Firestore timestamp objects (with seconds and nanoseconds)
      if (typeof date === 'object' && date !== null && 'seconds' in date) {
        return new Date(date.seconds * 1000).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
      } 
      
      // Handle regular Date objects or strings
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      if (dateObj instanceof Date && !isNaN(dateObj.getTime())) {
        return dateObj.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
      }
      
      // Fallback for any other format
      return typeof date === 'string' ? date : 'TBD';
    } catch (error) {
      console.error('Error formatting date:', date, error);
      return 'TBD';
    }
  };
  
  // Helper function to render markdown with images
  const renderMarkdownWithImages = (markdown: string) => {
    // First, extract image tags with alt text and URL
    const imagePattern = /!\[(.*?)\]\((.*?)\)/g;
    const extractedImages = [];
    let match;
    
    while ((match = imagePattern.exec(markdown)) !== null) {
      extractedImages.push({
        alt: match[1],
        url: match[2],
        fullMatch: match[0]
      });
    }
    
    // First convert markdown headers to HTML
    let html = markdown
      .replace(/^### (.*$)/gim, '<h3 style="color: #1976d2; margin-top: 25px; margin-bottom: 15px; font-size: 1.5rem;">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 style="color: #0d47a1; margin-top: 30px; margin-bottom: 15px; font-size: 1.8rem; border-bottom: 1px solid #eaeaea; padding-bottom: 8px;">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 style="color: #002171; margin-top: 30px; margin-bottom: 20px; font-size: 2.2rem; text-align: center;">$1</h1>');
    
    // Convert markdown images to HTML images with proper styling
    for (const img of extractedImages) {
      html = html.replace(
        img.fullMatch, 
        `<div class="newsletter-image" style="margin: 25px 0; text-align: center;">
          <img src="${img.url}" alt="${img.alt}" style="max-width:100%; width: 100%; height: auto; border-radius:8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); object-fit: cover; aspect-ratio: 16/9;" />
          <div style="font-style: italic; color: #666; margin-top: 8px; font-size: 0.9rem; text-align: center;">${img.alt}</div>
        </div>`
      );
    }
    
    // Convert markdown links to HTML links
    html = html.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" style="color: #1976d2; text-decoration: none; font-weight: 500;">$1</a>');
    
    // Convert markdown bold/italic to HTML
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
    
    // Convert markdown lists to HTML lists
    html = html.replace(/^\s*-\s*(.*?)$/gim, '<li style="margin-bottom: 8px;">$1</li>');
    
    // Wrap lists in ul tags
    html = html.replace(/<li.*?<\/li>/gm, (match) => {
      return `<ul style="padding-left: 20px; margin: 15px 0;">${match}</ul>`;
    });
    
    // Convert markdown line breaks to HTML paragraphs
    html = html.split('\n\n').map(para => {
      // Don't wrap headers, images, or lists in paragraphs
      if (para.trim() === '' || 
          para.startsWith('<h') || 
          para.startsWith('<div class="newsletter-image">') || 
          para.startsWith('<ul>')) {
        return para;
      }
      return `<p style="line-height: 1.6; margin: 12px 0; font-size: 1rem;">${para}</p>`;
    }).join('');
    
    // Convert markdown horizontal rules to HTML
    html = html.replace(/^---$/gim, '<hr style="border: 0; height: 1px; background: #eaeaea; margin: 30px 0;" />');
    
    // Wrap the entire newsletter in a container with proper styling
    html = `<div class="newsletter-container" style="font-family: 'Roboto', Arial, sans-serif; max-width: 800px; margin: 0 auto; color: #333; background: #fff; padding: 15px;">
      ${html}
    </div>`;
    
    return html;
  };
  
  // Helper function to handle copy to clipboard
  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(generatedContent || '');
    setSuccess(true);
  };
  
  // Helper function to handle format text
  const handleFormatText = (format: 'bold' | 'italic' | 'unordered-list') => {
    const textArea = document.createElement('textarea');
    textArea.value = generatedContent || '';
    
    switch (format) {
      case 'bold':
        textArea.value = `**${textArea.value}**`;
        break;
      case 'italic':
        textArea.value = `*${textArea.value}*`;
        break;
      case 'unordered-list':
        textArea.value = `- ${textArea.value}`;
        break;
      default:
        break;
    }
    
    setGeneratedContent(textArea.value);
  };
  
  // Save content to library
  const handleSaveToLibrary = async () => {
    if (!generatedContent || !selectedTemplate) return;

    setLoading(true);
    
    try {
      // Extract cover image for thumbnail
      let coverImageUrl = '';
      const coverImageMatch = generatedContent.match(/!\[(.*?)\]\((.*?)\)/);
      if (coverImageMatch && coverImageMatch.length > 2) {
        coverImageUrl = coverImageMatch[2];
      }
      
      // Create a content item with appropriate metadata
      const newAsset: MarketingAsset = {
        id: uuidv4(),
        name: `${selectedTemplate.name} - ${new Date().toLocaleDateString()}`,
        type: 'content',
        url: '', // No URL for content
        thumbnailUrl: coverImageUrl, // Use cover image as thumbnail
        organizationId,
        metadata: {
          prompt,
          aiProvider: 'stability',
          platform: contentType === 'social' ? 'facebook' : 
                   contentType === 'email' ? 'email' : 
                   contentType === 'ad' ? 'web' : 
                   contentType === 'newsletter' ? 'email' : 
                   contentType === 'blog' ? 'web' : 'web', // Use 'email' for newsletter, 'web' as fallback
          coverImage: coverImageUrl, // Add cover image URL for gallery display
          content: generatedContent, // Store the full content in metadata
        },
        tags: [contentType],
        createdAt: new Date().toISOString(),
        generatedBy: 'current-user-id', // This would be set to the actual user ID
      };
      
      // Add to Firestore via context
      const assetId = await addMarketingAsset(newAsset);
      
      if (assetId) {
        setSavedContent([...savedContent, newAsset]);
        setSuccess(true);

      }
    } catch (error) {
      console.error('Error saving newsletter:', error);
      setError('Failed to save newsletter');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Box>
      <Grid container spacing={3}>
        {/* Content Generation Form */}
        <Grid item xs={12} md={5}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" gutterBottom>
              Create Marketing Content
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="content-type-label">Content Type</InputLabel>
                  <Select
                    labelId="content-type-label"
                    id="content-type"
                    value={contentType}
                    label="Content Type"
                    onChange={handleContentTypeChange}
                  >
                    <MenuItem value="social">Social Media</MenuItem>
                    <MenuItem value="email">Email</MenuItem>
                    <MenuItem value="blog">Blog Post</MenuItem>
                    <MenuItem value="ad">Advertisement</MenuItem>
                    <MenuItem value="newsletter">Newsletter</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Newsletter Content Selection - Show only for newsletter content type */}
              {contentType === 'newsletter' && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>
                      Include in Newsletter
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={includeEvents} 
                          onChange={(e) => setIncludeEvents(e.target.checked)}
                        />
                      }
                      label="Events"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={includeTrips} 
                          onChange={(e) => setIncludeTrips(e.target.checked)}
                        />
                      }
                      label="Trips"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={includeMemberships} 
                          onChange={(e) => setIncludeMemberships(e.target.checked)}
                        />
                      }
                      label="Memberships"
                    />
                  </Grid>
                  
                  {/* Event Selection */}
                  {includeEvents && events.length > 0 && (
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel id="events-label">Select Events</InputLabel>
                        <Select
                          labelId="events-label"
                          id="events"
                          multiple
                          value={selectedEvents}
                          onChange={(e) => setSelectedEvents(e.target.value as string[])}
                          input={<OutlinedInput label="Select Events" />}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((value) => {
                                const event = events.find(e => e.id === value);
                                return <Chip key={value} label={event ? event.title : value} />;
                              })}
                            </Box>
                          )}
                        >
                          {events.map((event) => (
                            <MenuItem key={event.id} value={event.id}>
                              <Checkbox checked={selectedEvents.indexOf(event.id || '') > -1} />
                              <ListItemText 
                                primary={event.title} 
                                secondary={formatDate(event.startDate)}
                              />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  
                  {/* Trip Selection */}
                  {includeTrips && trips.length > 0 && (
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel id="trips-label">Select Trips</InputLabel>
                        <Select
                          labelId="trips-label"
                          id="trips"
                          multiple
                          value={selectedTrips}
                          onChange={(e) => setSelectedTrips(e.target.value as string[])}
                          input={<OutlinedInput label="Select Trips" />}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((value) => {
                                const trip = trips.find(t => t.id === value);
                                return <Chip key={value} label={trip ? trip.title : value} />;
                              })}
                            </Box>
                          )}
                        >
                          {trips.map((trip) => (
                            <MenuItem key={trip.id} value={trip.id}>
                              <Checkbox checked={selectedTrips.indexOf(trip.id || '') > -1} />
                              <ListItemText 
                                primary={trip.title} 
                                secondary={`${formatDate(trip.startDate)} - ${formatDate(trip.endDate)}`} 
                              />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  
                  {/* Membership Selection */}
                  {includeMemberships && memberships.length > 0 && (
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel id="memberships-label">Select Memberships</InputLabel>
                        <Select
                          labelId="memberships-label"
                          id="memberships"
                          multiple
                          value={selectedMemberships}
                          onChange={(e) => setSelectedMemberships(e.target.value as string[])}
                          input={<OutlinedInput label="Select Memberships" />}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((value) => {
                                const membership = memberships.find(m => m.id === value);
                                return <Chip key={value} label={membership ? membership.name : value} />;
                              })}
                            </Box>
                          )}
                        >
                          {memberships.map((membership) => (
                            <MenuItem key={membership.id} value={membership.id}>
                              <Checkbox checked={selectedMemberships.indexOf(membership.id) > -1} />
                              <ListItemText 
                                primary={membership.name} 
                                secondary={`$${membership.price} ${membership.currency}`} 
                              />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                </>
              )}
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Templates
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {contentTemplates[contentType].map((template) => (
                    <Paper
                      key={template.name}
                      elevation={0}
                      sx={{
                        p: 1.5,
                        cursor: 'pointer',
                        border: selectedTemplate?.name === template.name 
                          ? `2px solid ${theme.palette.primary.main}` 
                          : `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                      onClick={() => handleTemplateSelect(template)}
                    >
                      <Typography variant="body2" fontWeight="bold">
                        {template.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {template.description}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Prompt"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Describe what you want to generate..."
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="tone-label">Tone</InputLabel>
                  <Select
                    labelId="tone-label"
                    id="tone"
                    value={tone}
                    label="Tone"
                    onChange={handleToneChange}
                  >
                    <MenuItem value="formal">Formal</MenuItem>
                    <MenuItem value="casual">Casual</MenuItem>
                    <MenuItem value="professional">Professional</MenuItem>
                    <MenuItem value="friendly">Friendly</MenuItem>
                    <MenuItem value="persuasive">Persuasive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Word Count"
                  value={wordCount}
                  onChange={(e) => setWordCount(parseInt(e.target.value) || 150)}
                  InputProps={{
                    inputProps: { min: 50, max: 1000 }
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handleGenerateContent}
                  disabled={isGenerating || !prompt}
                  startIcon={isGenerating ? <CircularProgress size={20} /> : <AddIcon />}
                >
                  {isGenerating ? 'Generating...' : 'Generate Content'}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* Preview and Generated Content */}
        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, position: 'relative', minHeight: 400 }}>
            {isGenerating ? (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                minHeight: 300,
              }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Generating your content with AI...
                </Typography>
              </Box>
            ) : generatedContent ? (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                  <IconButton 
                    size="small" 
                    onClick={() => navigator.clipboard.writeText(generatedContent)}
                    sx={{ mr: 1 }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => setGeneratedContent(null)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Generated Content</Typography>
                  <Box>
                    <IconButton size="small" onClick={() => handleFormatText('bold')}>
                      <FormatBoldIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleFormatText('italic')}>
                      <FormatItalicIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleFormatText('unordered-list')}>
                      <FormatListBulletedIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                <Box 
                  className="markdown-content"
                  sx={{
                    p: 2,
                    maxHeight: 500,
                    overflowY: 'auto',
                    whiteSpace: 'pre-wrap',
                    '& img': {
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: 1,
                      my: 2,
                    },
                    '& h1, & h2, & h3': {
                      color: branding.primaryColor || theme.palette.primary.main,
                      fontFamily: branding.fontFamily || 'inherit',
                    },
                    '& a': {
                      color: branding.secondaryColor || theme.palette.secondary.main,
                      textDecoration: 'none',
                    },
                    '& hr': {
                      my: 2,
                      borderColor: theme.palette.divider,
                    },
                  }}
                  dangerouslySetInnerHTML={{ 
                    __html: renderMarkdownWithImages(generatedContent)
                  }}
                />
                
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
                  <Button 
                    variant="contained" 
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveToLibrary}
                  >
                    Save to Library
                  </Button>
                  <Button 
                    variant="outlined"
                    startIcon={<ContentCopyIcon />}
                    onClick={handleCopyToClipboard}
                  >
                    Copy to Clipboard
                  </Button>
                </Box>
              </>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center',
                height: '100%',
                minHeight: 300,
                border: `1px dashed ${theme.palette.divider}`,
                borderRadius: '8px',
                backgroundColor: theme.palette.background.default
              }}>
                <Typography variant="body1" color="text.secondary">
                  Your generated content will appear here
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Select a template and customize your prompt to get started
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
        
        {/* Content Library */}
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2, mt: 3, border: `1px solid ${theme.palette.divider}` }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Content Library</Typography>
            </Box>
            
            <Tabs
              value={contentTabValue}
              onChange={handleContentTabChange}
              textColor="primary"
              indicatorColor="primary"
              aria-label="content library tabs"
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
            >
              <Tab label="All Content" />
              <Tab label="Social" />
              <Tab label="Email" />
              <Tab label="Blog" />
              <Tab label="Ads" />
              <Tab label="Newsletters" />
            </Tabs>
            
            {savedContent.length === 0 ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center',
                height: '200px',
                border: `1px dashed ${theme.palette.divider}`,
                borderRadius: '8px',
                backgroundColor: theme.palette.background.default
              }}>
                <Typography variant="body1" color="text.secondary">
                  No content found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Generate your first content to get started
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {savedContent.map((content) => (
                  <Grid item xs={12} sm={6} md={4} key={content.id}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography gutterBottom variant="subtitle1" component="div">
                          {content.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {content.metadata.prompt}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                          {content.tags.map((tag) => (
                            <Chip
                              key={tag}
                              label={tag}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                        {content.metadata.coverImage && (
                          <Box sx={{ mt: 2 }}>
                            <img src={content.metadata.coverImage} alt="Cover Image" style={{ width: '100%', height: 150, objectFit: 'cover', borderRadius: 8 }} />
                          </Box>
                        )}
                      </CardContent>
                      <CardActions>
                        <Button 
                          size="small" 
                          startIcon={<ContentCopyIcon />}
                          onClick={() => {
                            // Copy content to clipboard
                            if (content.metadata.content) {
                              navigator.clipboard.writeText(content.metadata.content);
                              setSnackbarMessage('Content copied to clipboard');
                              setSnackbarOpen(true);
                            }
                          }}
                        >
                          Copy
                        </Button>
                        <Button 
                          size="small" 
                          startIcon={<DeleteIcon />}
                          color="error"
                          onClick={() => {
                            // Delete content from library
                            deleteMarketingAsset(content.id);
                            setSavedContent(prev => prev.filter(item => item.id !== content.id));
                            setSnackbarMessage('Content deleted from library');
                            setSnackbarOpen(true);
                          }}
                        >
                          Delete
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>
        </Grid>
        
        {/* Success/Error notifications */}
        <Snackbar open={success} autoHideDuration={6000} onClose={() => setSuccess(false)}>
          <Alert onClose={() => setSuccess(false)} severity="success" sx={{ width: '100%' }}>
            {generatedContent ? 'Content generated successfully!' : 'Content copied to clipboard!'}
          </Alert>
        </Snackbar>
        
        <Snackbar 
          open={!!error} 
          autoHideDuration={6000} 
          onClose={() => setError(null)}
        >
          <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
        
        <Snackbar 
          open={snackbarOpen} 
          autoHideDuration={6000} 
          onClose={() => setSnackbarOpen(false)}
        >
          <Alert onClose={() => setSnackbarOpen(false)} severity="info" sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Grid>
      
      {/* Loading indicator */}
      {loadingData && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(255, 255, 255, 0.5)' }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default ContentGenerator;
